import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Send, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandifyContact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    service_type: "marketing",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        company: "",
        service_type: "marketing",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] brandify-body">
      <SEO brand="brandify" path="/brandify/contact" title="Contact" description="Start a project with Brandify — AI-powered marketing for ambitious brands." />
      <Navigation brand="brandify" />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="heading-font text-5xl md:text-7xl text-white mb-6" data-testid="contact-heading">
              LET'S
              <span className="text-[#1cb5ae]"> CONNECT</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Ready to accelerate your growth? Tell us about your project and we'll show you what's possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="heading-font text-3xl text-white mb-8">GET IN TOUCH</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-[#1cb5ae] mt-1" />
                  <div>
                    <div className="text-white font-medium mb-1">Email</div>
                    <a href="mailto:hello@brandifyofficial.com" className="text-gray-400 hover:text-[#1cb5ae] transition-colors">
                      hello@brandifyofficial.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-[#1cb5ae] mt-1" />
                  <div>
                    <div className="text-white font-medium mb-1">Location</div>
                    <div className="text-gray-400">Global - Remote First</div>
                  </div>
                </div>
              </div>

              <div className="glass-effect rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-white mb-4">What to Expect</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#1cb5ae] mt-2"></div>
                    <span>Response within 24 hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#1cb5ae] mt-2"></div>
                    <span>Free initial consultation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#1cb5ae] mt-2"></div>
                    <span>Custom strategy proposal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#1cb5ae] mt-2"></div>
                    <span>No pressure, no obligation</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <form onSubmit={handleSubmit} className="glass-effect rounded-2xl p-8" data-testid="contact-form">
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-white mb-2 block">Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="bg-[#0A0A0A] border-[#262626] text-white"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-white mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="bg-[#0A0A0A] border-[#262626] text-white"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-white mb-2 block">Company</Label>
                    <Input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="bg-[#0A0A0A] border-[#262626] text-white"
                      data-testid="contact-company-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service_type" className="text-white mb-2 block">Service Interest *</Label>
                    <select
                      id="service_type"
                      value={formData.service_type}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                      required
                      className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] text-white rounded-md"
                      data-testid="contact-service-select"
                    >
                      <option value="marketing">Marketing & Growth</option>
                      <option value="tech">Tech & Development</option>
                      <option value="ai">AI Solutions</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white mb-2 block">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="bg-[#0A0A0A] border-[#262626] text-white"
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#1cb5ae] hover:bg-[#1aada5] text-white py-6 rounded-full font-medium text-lg"
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer brand="brandify" />
    </div>
  );
};

export default BrandifyContact;